<template>
  <div v-if="hasAccess(MARKETING_MATERIALS_ACCESS, MARKETING_MATERIALS_ACCESS_MANAGE)"
       class="marketing-materials fullPage" ref="fullPage">
    <h1>Full Page Widget</h1>
    <template v-if="pages && pages.length === 0">
      <notification-financing-page/>
    </template>
    <template v-else-if="pages && isShowBlock">
      <GetCodeWidget
          :active="codeModalActive"
          :code="code"
          :email-sending="emailSending"
          :nameWidjet="fullPage"
          :nameWidjetFull="fullPageFull"
          @submit="submit"
          @close="closeModal"/>

      <PageSelector />

      <div class="columns">
        <div class="column full-page-widget-title">
          <p>
            This Full Page Widget will look and feel like a web page of your website.
            Use it to tell your customers about available financing options and help
            them calculate their estimated monthly payment.
          </p>
          <p>
            Choose the widget color to fit your website style and embed the widget in your website.
          </p>
        </div>
      </div>
      <div class="choose">
        <p class="title">Choose widget color:</p>
        <div class="chooseOptions">
          <button v-for="(option, key) in options" :key="key"
                  v-bind:class="{ active: selected === key }"
                  @click="optionClickHandler(key)">
            <span :style="{ background: getGradient(option.color1, option.color2) }"></span>
          </button>
        </div>
        <div class="chooseSwitch">
          <div class="chooseSwitchText">
            Show 'See Your Personal Offers' button
            <b-tooltip label="You can choose to show or hide
                              the ‘See Your Personal Offers’ button. This button is
                              switched on by default."
                       class="block-tooltip is-gray"
                       type="is-info"
                       size="is-small"
                       :delay="200"
                       :position="getTooltipPositionByWindowWidth(570, 'is-top')"
                       animated
                       multilined>
              <b-icon pack="fa" size="is-small" icon="question-circle">
              </b-icon>
            </b-tooltip>
          </div>
          <b-switch v-model="hideLink"
                    size="is-small"
                    class="enh-switch"
                    type="is-green"
                    position="is-right">
          </b-switch>
        </div>
        <div class="buttons">
          <b-button class="btn_preview" type="is-success"
                    @click.prevent="showPreviewFullPage">
            Preview Widget
          </b-button>
          <b-button
              type="is-success"
              class="get-full-page-widget-code"
              @click.prevent="showFullPageModal"
          >
            Get Widget Code
          </b-button>
        </div>
      </div>
      <div class="column">
      </div>
    </template>
  </div>
</template>

<script>
import {
  mapActions,
  mapGetters,
  mapMutations,
  mapState,
} from 'vuex';
import * as CONTRACTOR_ACTIONS from '@/store/modules/contractor/actions';
import * as MUTATIONS from '@/store/mutations';
import {
  fullPageSendToEmail,
  fullPage,
  showBlock,
} from '@/api/widget';
import NotificationFinancingPage from '@/components/NotificationFinancingPage.vue';
import { getTooltipPositionByWindowWidth } from '@/utils/getTooltipPosition';
import GetCodeWidget from '@/components/Modal/GetCodeWidget.vue';
import PageSelector from '@/views/MarketingMaterials/components/PageSelector.vue';

export default {
  name: 'FullPageWidget',
  components: {
    PageSelector,
    GetCodeWidget,
    NotificationFinancingPage,
  },
  data: () => ({
    siteUrl: null,
    options: {
      default: {
        color1: '#68BA62',
        color2: '#1C418C',
        coBrandedColor: '#FFFFFF',
      },
      'red/blue': {
        color1: '#B31417',
        color2: '#064C8F',
        coBrandedColor: '#FFFFFF',
      },
      'orange/blue': {
        color1: '#FF531B',
        color2: '#054078',
        coBrandedColor: '#FFFFFF',
      },
      'gold/black': {
        color1: '#BD921E',
        color2: '#3A3A37',
        coBrandedColor: '#FFFFFF',
      },
      'yellow/purple': {
        color1: '#FFD200',
        color2: '#472152',
        coBrandedColor: '#293033',
      },
    },
    selected: 'default',
    hideLink: true,
    codeLoading: false,
    codeModalActive: false,
    emailSending: false,
    code: null,
    fullPage: 'Full Page',
    fullPageFull: 'Full Page',
    isShowBlock: false,
    MARKETING_MATERIALS_ACCESS: { resource: 'marketing-materials', code: 'access' },
    MARKETING_MATERIALS_ACCESS_MANAGE: { resource: 'marketing-materials', code: 'manage_access' },
  }),
  computed: {
    ...mapState('contractor', { contractorId: (state) => state.id }),
    ...mapState('user', { userId: (state) => state.id }),
    ...mapGetters(['hasAccess']),
    ...mapGetters('contractor', ['selectedPage', 'pages']),
    windowWidth() {
      return this.$store.state.windowWidth;
    },
  },
  async activated() {
    if (!this.contractorId) return;
    this.startLoading();

    try {
      const { data } = await showBlock(this.userId);
      this.isShowBlock = data.show;
      if (!this.selectedPage?.id) {
        await this.updPages(this.contractorId);
      }
      window.scrollTo(0, 0);
    } catch (error) {
      this.$buefy.snackbar.open({
        message: 'An unexpected error occurred, please contact support@enhancify.com',
        actionText: ' ',
        position: 'is-top',
        indefinite: true,
        type: 'is-danger',
      });
    }

    this.finishLoading();
  },
  methods: {
    getTooltipPositionByWindowWidth,
    ...mapActions('contractor', { updPages: CONTRACTOR_ACTIONS.LOAD_PAGES }),
    ...mapMutations({
      startLoading: MUTATIONS.START_LOADING,
      finishLoading: MUTATIONS.FINISH_LOADING,
    }),
    ...mapActions('contractor', { setSelectedPage: CONTRACTOR_ACTIONS.SET_SELECTED_PAGE }),

    getGradient(color1, color2) {
      return `linear-gradient(90deg, ${color1}, ${color1} 50%, ${color2} 50%)`;
    },
    optionClickHandler(key) {
      this.selected = key;
    },
    closeModal() {
      this.code = null;
      this.codeModalActive = false;
    },
    openCodeModal() {
      this.codeModalActive = true;
    },
    showPreviewFullPage() {
      const color1 = this.options[this.selected].color1.replace('#', '$');
      const color2 = this.options[this.selected].color2.replace('#', '$');
      const coBrandedColor = this.options[this.selected].coBrandedColor.replace('#', '$');
      const hideLink = !this.hideLink ? 1 : 0;
      if (this.contractorId === null || !this.selectedPage?.id) return;
      window.open(`${process.env.VUE_APP_SERVER_BASE_URI}/widgets/full-page/${this.selectedPage.id}/preview?userId=${this.userId}&color1=${color1}&color2=${color2}&coBrandedColor=${coBrandedColor}&hideLink=${hideLink}`, '_blank');
    },
    showFullPageModal() {
      if (this.contractorId === null || !this.selectedPage?.id) return;
      this.codeLoading = true;
      const formData = new FormData();
      formData.set('userId', this.userId);
      formData.set('pageId', this.selectedPage.id);
      formData.set('color1', this.options[this.selected].color1.replace('#', '$'));
      formData.set('color2', this.options[this.selected].color2.replace('#', '$'));
      formData.set('coBrandedColor', this.options[this.selected].coBrandedColor.replace('#', '$'));
      formData.set('hideLink', !this.hideLink ? 1 : 0);

      fullPage(formData)
        .then((response) => {
          if (!response.data) {
            this.$buefy.toast.open({ message: 'An unexpected error occurred, please contact support@enhancify.com', type: 'is-danger', duration: 3000 });
            return;
          }
          this.code = response.data;
          this.openCodeModal();
        })
        .catch(() => this.$buefy.toast.open({ message: 'An unexpected error occurred, please contact support@enhancify.com', type: 'is-danger', duration: 3000 }))
        .finally(() => { this.codeLoading = false; });
    },
    submit(email) {
      if (this.contractorId === null || !this.selectedPage?.id) return;
      this.emailSending = true;
      const formData = new FormData();
      formData.set('userId', this.userId);
      formData.set('pageId', this.selectedPage.id);
      formData.set('color1', this.options[this.selected].color1);
      formData.set('color2', this.options[this.selected].color2);
      formData.set('coBrandedColor', this.options[this.selected].coBrandedColor);
      formData.set('hideLink', !this.hideLink ? 1 : 0);
      formData.set('email', email);
      fullPageSendToEmail(formData)
        .then((response) => {
          if (response.status === 200) {
            this.$buefy.toast.open({ message: 'Success!', type: 'is-success', duration: 3000 });
          }
        })
        .catch(() => this.$buefy.toast.open({ message: 'An unexpected error occurred, please contact support@enhancify.com', type: 'is-danger', duration: 3000 }))
        .finally(() => { this.emailSending = false; });
    },
  },
};
</script>

<style lang="stylus" scoped>
.full-page-widget-title
  max-width 860px

.choose.block-title
  margin-bottom 0.5rem

.fullPage
  p.help
    padding-top 0
  .choose
    .title
      font-weight 400
      font-size 16px
      line-height 24px
      margin-top 30px
      margin-bottom 0
      color #969ea2
    .chooseOptions
      button
        width 54px
        height 54px
        border 1px solid rgba(143, 167, 179, 0.298039)
        border-radius 3px
        padding 10px
        cursor pointer
        margin-top: 13.5px
        background-color #ffffff
        &:not(:last-child)
          margin-right 13.5px
        &.active,
        &:hover
          border-color #68BA62
        span
          display block
          width 100%
          height 100%
          border-radius 100%
    .chooseSwitch
      display flex
      margin-top: 30px
      .chooseSwitchText
        display inline-flex
        align-items: center
        margin-right: 20px
        color #969ea2
      >>>.switch.enh-switch
        span.check
          &:before
            box-shadow none
    .buttons
      margin-top 25px
      .btn_preview
        background-color transparent
        border-color #68BA62
        color #68BA62
        &:hover
          border-color transparent
          color #fff
        &:active
         background-color transparent
         border-color #68BA62
         color #68BA62
        &:focus
        &.is-focused
         -webkit-box-shadow none
         box-shadow none
        @media screen and (max-width: 499px)
          margin-right 0
      button
        @media screen and (max-width: 499px)
          width 100%
        font-size 19px
        height 48px
        text-transform uppercase
        &:hover
          background-color #17B548
</style>
